/**
 * NovoLeadForm — formulario de criacao de lead (dentro de SlideOverPanel).
 *
 * 1o campo (REF2-4, opcional): vincular a um Cliente existente. Ao selecionar,
 * o contato do lead e TROCADO pelo do cliente (busca via listarClientes com
 * debounce; detalhe via obterCliente): nome inteiro em "Nome completo",
 * Apelido vazio e editavel, e-mail/telefone vazios quando o cliente nao tem
 * (CRM-20/21 — herdar do cliente anterior mandava a proposta de B para A).
 * O vinculo segue opcional — sem cliente, o lead nasce pre-cliente.
 *
 * Demais campos (item 9): nome/sobrenome do contato + Descricao (titulo,
 * obrigatorio) + telefone + email + origem + valor estimado + responsavel
 * (opcional; vazio = backend atribui ao usuario atual) + observacoes.
 *
 * Proxima acao + data (CRM-24): o lead nasce com "Primeiro contato" no
 * proximo dia util — e assim que ele entra no sino e em Minhas Pendencias.
 * Temperatura e opcional. Responsaveis vem de `useResponsaveisCrm`
 * (CRM-22), nao mais da lista derivada dos cards do board.
 *
 * Endereco (ADR-0071, opcional): EnderecosEditor com um cartao inicial em
 * branco — o CEP preenche logradouro/bairro/cidade/UF. Cartao que ninguem
 * preencheu nao vai ao backend; os demais seguem em `enderecos` no POST.
 *
 * `etapaNome` (opcional) so e exibido como contexto quando o form e aberto
 * pelo botao "+ Adicionar" de uma coluna especifica.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";

import AvisoContatoExistente, {
  useContatoExistente,
} from "../../components/AvisoContatoExistente";
import EnderecosEditor, { novoEndereco } from "../../components/EnderecosEditor";
import { useAuth } from "../../context/AuthContext";
import useResponsaveisCrm from "../../hooks/useResponsaveisCrm";
import {
  listarClientes,
  listarRepresentantes,
  obterCliente,
} from "../../services/comercial";
import { ORIGENS_LEAD, TEMPERATURAS, possiveisDuplicados } from "../../services/crm";
import { buscarCep } from "../../services/enderecos";
import { formatarDinheiro, parseDinheiro } from "../../utils/dinheiro";
import { CAMPO_GERAL, errosPorCampo } from "../../utils/errosApi";
import {
  capitalizarNome,
  mascaraTelefone,
  moedaParaNumero,
} from "../../utils/leadFormat";

import {
  errosDeEnderecoDaResposta,
  prepararEnderecos,
  validarEnderecosDoLead,
} from "./enderecosLead";
import { PROXIMA_ACAO_PADRAO, proximoDiaUtil } from "./proximaAcao";
import {
  Field,
  FormStack,
  InlineError,
  InlineHint,
  Input,
  Select,
  Textarea,
} from "./styles";
import { EmptyMsg, InfoMsg } from "../Orcamentos/styles";

// Campos que o form mostra: erro de outra chave vai para o aviso do topo,
// para nenhuma mensagem do backend sumir da tela (CRM-40).
const MSG_SEM_CONTATO_NAO_CONVERTE =
  "Sem nome e telefone ou e-mail, este lead não poderá virar cliente.";

const CAMPOS_NA_TELA = [
  "titulo", "cliente", "contato_nome", "contato_sobrenome", "contato_telefone",
  "contato_email", "origem", "origem_outro", "valor_estimado", "responsavel",
  "parceiros", "observacoes", "proxima_acao", "data_proxima_acao", "temperatura",
];

/** Erro por campo do 400, com o que a tela não mostra no aviso geral. */
function errosDoLead(err) {
  const erros = errosPorCampo(err);
  const semCampoNaTela = Object.keys(erros).filter(
    (campo) => campo !== CAMPO_GERAL && !CAMPOS_NA_TELA.includes(campo)
  );
  if (!semCampoNaTela.length) return erros;
  const naTela = Object.fromEntries(
    Object.entries(erros).filter(([campo]) => !semCampoNaTela.includes(campo))
  );
  naTela[CAMPO_GERAL] = [erros[CAMPO_GERAL], ...semCampoNaTela.map((campo) => erros[campo])]
    .filter(Boolean)
    .join(" ");
  return naTela;
}

function ErroDoCampo({ campo, erros }) {
  if (!erros[campo]) return null;
  return <InlineError id={`novo-lead-${campo}-erro`}>{erros[campo]}</InlineError>;
}

const MSG_VALOR_NEGATIVO = "Valor estimado não pode ser negativo.";

function NovoLeadForm({
  onSubmit,
  onCancel,
  loading = false,
  etapaNome,
}) {
  const { request, usuario } = useAuth();
  const { responsaveis } = useResponsaveisCrm();
  // CRM-12: aviso (nunca bloqueio) de contato ja cadastrado, no blur.
  const consultarContato = useCallback(
    (params) => possiveisDuplicados(request, params),
    [request]
  );
  const contatoExistente = useContatoExistente(consultarContato);

  const [valores, setValores] = useState(() => ({
    titulo: "",
    contato_nome: "",
    contato_sobrenome: "",
    contato_telefone: "",
    contato_email: "",
    origem: "",
    origem_outro: "",
    valor_estimado: "",
    responsavel: usuario?.id || "",
    observacoes: "",
    proxima_acao: PROXIMA_ACAO_PADRAO,
    data_proxima_acao: proximoDiaUtil(),
    temperatura: "",
  }));
  const [erros, setErros] = useState({});

  // ----- Enderecos titulados (ADR-0071, opcional) -----
  // Cartao inicial em branco: quem tem o CEP ja comeca por ele. Se ninguem
  // preencher, `prepararEnderecos` o deixa fora do POST.
  const [enderecos, setEnderecos] = useState(() => [novoEndereco()]);
  const [errosEnderecos, setErrosEnderecos] = useState([]);
  const buscarEndereco = useCallback((cep) => buscarCep(request, cep), [request]);

  // Depois de uma tentativa com erro, revalida a cada alteracao: o aviso some
  // quando a pessoa corrige e acompanha o cartao quando ela reordena ou remove.
  const alterarEnderecos = (lista) => {
    setEnderecos(lista);
    if (errosEnderecos.length) setErrosEnderecos(validarEnderecosDoLead(lista));
  };

  // ----- Vinculo com cliente existente (opcional) -----
  const [clienteSel, setClienteSel] = useState(null);
  const [busca, setBusca] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // ----- Parceiros indicadores (ID 26, opcional, multi) -----
  // Um lead pode ter varios parceiros (arquiteto, decorador...). A soma das
  // comissoes vira o RT do orcamento gerado a partir do lead.
  const [repsDisponiveis, setRepsDisponiveis] = useState([]);
  const [parceirosSel, setParceirosSel] = useState([]);

  useEffect(() => {
    let ativo = true;
    listarRepresentantes(request, { pageSize: 200 }, { silentError: true })
      .then((pagina) => {
        if (!ativo) return;
        const lista = Array.isArray(pagina) ? pagina : pagina?.results || [];
        setRepsDisponiveis(lista.filter((r) => r?.ativo !== false));
      })
      .catch(() => ativo && setRepsDisponiveis([]));
    return () => {
      ativo = false;
    };
  }, [request]);

  const opcoesParceiro = useMemo(
    () =>
      repsDisponiveis.filter(
        (r) => !parceirosSel.some((p) => String(p.id) === String(r.id)),
      ),
    [repsDisponiveis, parceirosSel],
  );

  const adicionarParceiro = (id) => {
    const rep = repsDisponiveis.find((r) => String(r.id) === String(id));
    if (rep && !parceirosSel.some((p) => String(p.id) === String(rep.id))) {
      setParceirosSel((prev) => [...prev, rep]);
    }
  };

  const removerParceiro = (id) =>
    setParceirosSel((prev) => prev.filter((p) => String(p.id) !== String(id)));

  const setCampo = (campo, valor) =>
    setValores((prev) => ({ ...prev, [campo]: valor }));
  // O campo que perdeu o foco ainda nao esta em `valores` no mesmo tick.
  const verificarContato = ({ email, telefone }) =>
    contatoExistente.verificar({
      email: email ?? valores.contato_email,
      telefone: telefone ?? valores.contato_telefone,
    });

  const atributosDoCampo = (campo) => ({
    id: `novo-lead-${campo}`,
    "aria-invalid": Boolean(erros[campo]),
    "aria-describedby": erros[campo] ? `novo-lead-${campo}-erro` : undefined,
  });

  // Vendedor responsável: por padrao quem esta criando (editavel). Pre-seleciona
  // o usuario logado assim que disponivel (sem sobrescrever escolha manual).
  useEffect(() => {
    if (usuario?.id) {
      setValores((prev) => (prev.responsavel ? prev : { ...prev, responsavel: usuario.id }));
    }
  }, [usuario]);

  // Opcoes do select: eu (sempre, no topo) + os demais usuarios ativos da
  // unidade (CRM-22), para poder atribuir a qualquer vendedor.
  const opcoesResponsavel = useMemo(() => {
    const lista = [];
    if (usuario?.id) {
      lista.push({ id: usuario.id, nome: `${usuario.nome || usuario.email || "Eu"} (você)` });
    }
    responsaveis.forEach((r) => {
      if (r?.id && String(r.id) !== String(usuario?.id)) lista.push(r);
    });
    return lista;
  }, [usuario, responsaveis]);

  // Busca de clientes com debounce (so quando ainda nao ha cliente vinculado).
  useEffect(() => {
    if (clienteSel) return undefined;
    const termo = busca.trim();
    if (termo.length < 2) {
      setResultados([]);
      return undefined;
    }
    let ativo = true;
    setBuscando(true);
    const id = setTimeout(() => {
      listarClientes(request, { busca: termo, pageSize: 8 })
        .then((p) => {
          if (!ativo) return;
          setResultados(Array.isArray(p) ? p : p?.results || []);
        })
        .catch(() => ativo && setResultados([]))
        .finally(() => ativo && setBuscando(false));
    }, 350);
    return () => {
      ativo = false;
      clearTimeout(id);
    };
  }, [busca, clienteSel, request]);

  // Id do cliente vinculado AGORA: a resposta de obterCliente de um cliente
  // que ja foi desvinculado chega atrasada e nao pode sobrescrever o atual.
  const clienteVinculadoRef = useRef(null);

  // Vincula o cliente e troca o contato do lead pelo dele, por inteiro.
  const vincularCliente = async (cliente) => {
    clienteVinculadoRef.current = cliente.id;
    setClienteSel(cliente);
    setResultados([]);
    setBusca("");
    // O contato E o do cliente: campo que ele nao tem fica vazio (nao herda
    // do cliente anterior). O nome vai inteiro; Apelido e "como prefere ser
    // chamado", nao sobrenome, entao fica vazio para a pessoa preencher.
    const aplicar = (fonte) => {
      setValores((prev) => ({
        ...prev,
        contato_nome: fonte.nome || "",
        contato_sobrenome: "",
        contato_email: fonte.email || "",
        contato_telefone: fonte.telefone || "",
      }));
    };
    aplicar(cliente);
    try {
      const detalhe = await obterCliente(request, cliente.id, { silentError: true });
      if (detalhe && clienteVinculadoRef.current === cliente.id) aplicar(detalhe);
    } catch (_) {
      /* resumo ja preencheu o essencial */
    }
  };

  const desvincularCliente = () => {
    clienteVinculadoRef.current = null;
    setClienteSel(null);
    setBusca("");
    setResultados([]);
    // Mantem os dados ja preenchidos (usuario pode ajustar) — so solta o vinculo.
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErros({});
    const errosDosEnderecos = validarEnderecosDoLead(enderecos);
    setErrosEnderecos(errosDosEnderecos);
    if (!valores.titulo.trim()) {
      setErros({ titulo: "A descrição é obrigatória." });
      return;
    }
    if (errosDosEnderecos.length) return;
    const valorNum = moedaParaNumero(valores.valor_estimado);
    if (valorNum != null && valorNum < 0) {
      setErros({ valor_estimado: MSG_VALOR_NEGATIVO });
      return;
    }
    // Monta payload omitindo campos vazios (backend trata ausencia melhor
    // que string vazia em FKs/decimais).
    const payload = { titulo: valores.titulo.trim() };
    if (clienteSel) payload.cliente = clienteSel.id;
    if (valores.contato_nome.trim()) payload.contato_nome = valores.contato_nome.trim();
    if (valores.contato_sobrenome.trim()) payload.contato_sobrenome = valores.contato_sobrenome.trim();
    if (valores.contato_telefone.trim()) payload.contato_telefone = valores.contato_telefone.trim();
    if (valores.contato_email.trim()) payload.contato_email = valores.contato_email.trim();
    if (valores.origem) payload.origem = valores.origem;
    if (valores.origem === "OUTRO" && valores.origem_outro.trim()) {
      payload.origem_outro = valores.origem_outro.trim();
    }
    if (valorNum != null) payload.valor_estimado = valorNum;
    if (valores.responsavel) payload.responsavel = valores.responsavel;
    if (valores.observacoes.trim()) payload.observacoes = valores.observacoes.trim();
    // Proxima acao: e o que poe o lead no sino. Vazio = lead sem follow-up.
    if (valores.proxima_acao.trim()) payload.proxima_acao = valores.proxima_acao.trim();
    if (valores.data_proxima_acao) payload.data_proxima_acao = valores.data_proxima_acao;
    if (valores.temperatura) payload.temperatura = valores.temperatura;
    if (parceirosSel.length) payload.parceiros = parceirosSel.map((p) => p.id);
    // Sem endereco preenchido a chave nem vai: o lead nasce sem endereco.
    const { enderecos: enderecosDoPayload } = prepararEnderecos(enderecos);
    if (enderecosDoPayload.length) payload.enderecos = enderecosDoPayload;

    try {
      await onSubmit(payload);
    } catch (err) {
      // Endereco recusado: o 400 aponta o item enviado; mostra no cartao.
      const errosDaApi = errosDeEnderecoDaResposta(err, enderecos);
      if (errosDaApi) setErrosEnderecos(errosDaApi);
      // Campo a campo pelo helper unico (CRM-40): a mensagem aparece embaixo
      // do campo com erro, e o que nao e de campo vai no aviso do topo.
      setErros(errosDoLead(err));
    }
  };

  // CRM-18: aviso sem bloqueio — a conversao (orcamento ou Convertido) exige
  // nome e telefone ou e-mail; melhor saber agora do que na hora da venda.
  const semContatoParaConverter =
    !valores.contato_nome.trim()
    || (!valores.contato_telefone.trim() && !valores.contato_email.trim());

  return (
    <form onSubmit={handleSubmit}>
      <FormStack>
        {erros[CAMPO_GERAL] && <InlineError role="alert">{erros[CAMPO_GERAL]}</InlineError>}

        {etapaNome && (
          <InlineHint>
            Novo lead na etapa <strong>{etapaNome}</strong>.
          </InlineHint>
        )}

        {/* Vinculo com cliente existente (opcional) — primeiro campo */}
        <Field>
          Vincular a cliente existente (opcional)
          {clienteSel ? (
            <InfoMsg style={{ justifyContent: "space-between", alignItems: "center" }}>
              <span>
                Vinculado: <strong>{clienteSel.nome}</strong>
                {clienteSel.documento ? ` · ${clienteSel.documento}` : ""}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--icon"
                onClick={desvincularCliente}
                aria-label="Desvincular cliente"
                title="Desvincular"
              >
                <FiX size={14} aria-hidden="true" />
              </button>
            </InfoMsg>
          ) : (
            <>
              <div className="input-prefixed">
                <FiSearch size={14} aria-hidden="true" />
                <input
                  type="search"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar cliente por nome, documento, e-mail..."
                />
              </div>
              {buscando ? (
                <EmptyMsg style={{ padding: 8 }}>Buscando...</EmptyMsg>
              ) : busca.trim().length >= 2 && resultados.length === 0 ? (
                <EmptyMsg style={{ padding: 8 }}>
                  Nenhum cliente encontrado.
                </EmptyMsg>
              ) : resultados.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-md)",
                    padding: 4,
                    maxHeight: 200,
                    overflowY: "auto",
                  }}
                >
                  {resultados.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => vincularCliente(c)}
                      style={{
                        textAlign: "left",
                        background: "transparent",
                        border: 0,
                        borderRadius: "var(--radius-sm)",
                        padding: "8px 10px",
                        cursor: "pointer",
                        font: "inherit",
                        color: "var(--ink)",
                      }}
                    >
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.nome}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {c.documento || c.tipo_display || c.tipo || "—"}
                        {c.email ? ` · ${c.email}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <InlineHint>
                  Deixe em branco para criar um lead a partir de um contato novo.
                </InlineHint>
              )}
            </>
          )}
        </Field>

        {clienteSel && (
          <InlineHint>
            Nome, telefone e e-mail vêm do cliente vinculado — remova o vínculo
            para editar. O apelido é livre.
          </InlineHint>
        )}
        {!clienteSel && semContatoParaConverter && (
          <InlineHint role="status">{MSG_SEM_CONTATO_NAO_CONVERTE}</InlineHint>
        )}

        <Field>
          Nome completo
          <Input
            {...atributosDoCampo("contato_nome")}
            type="text"
            value={valores.contato_nome}
            onChange={(e) => setCampo("contato_nome", e.target.value)}
            onBlur={(e) => setCampo("contato_nome", capitalizarNome(e.target.value))}
            placeholder="Nome completo da pessoa"
            maxLength={200}
            disabled={Boolean(clienteSel)}
          />
          <ErroDoCampo campo="contato_nome" erros={erros} />
        </Field>

        <Field>
          Apelido
          <Input
            {...atributosDoCampo("contato_sobrenome")}
            type="text"
            value={valores.contato_sobrenome}
            onChange={(e) => setCampo("contato_sobrenome", e.target.value)}
            onBlur={(e) => setCampo("contato_sobrenome", capitalizarNome(e.target.value))}
            placeholder="Como prefere ser chamado"
            maxLength={200}
          />
          <ErroDoCampo campo="contato_sobrenome" erros={erros} />
        </Field>

        <Field>
          Descrição *
          <Input
            {...atributosDoCampo("titulo")}
            type="text"
            value={valores.titulo}
            onChange={(e) => setCampo("titulo", e.target.value)}
            placeholder="Ex.: Escreva aqui uma descrição"
            maxLength={200}
            required
          />
          <ErroDoCampo campo="titulo" erros={erros} />
        </Field>

        <Field>
          Telefone do contato
          <Input
            {...atributosDoCampo("contato_telefone")}
            type="tel"
            value={valores.contato_telefone}
            onChange={(e) => setCampo("contato_telefone", e.target.value)}
            // Mascara no blur (ADR-0059): no onChange ela cortava o +55.
            onBlur={(e) => {
              setCampo("contato_telefone", mascaraTelefone(e.target.value));
              verificarContato({ telefone: e.target.value });
            }}
            placeholder="(00) 00000-0000"
            maxLength={40}
            disabled={Boolean(clienteSel)}
          />
          <ErroDoCampo campo="contato_telefone" erros={erros} />
        </Field>

        <Field>
          E-mail do contato
          <Input
            {...atributosDoCampo("contato_email")}
            type="email"
            value={valores.contato_email}
            onChange={(e) => setCampo("contato_email", e.target.value)}
            onBlur={(e) => verificarContato({ email: e.target.value })}
            placeholder="contato@email.com"
            maxLength={200}
            disabled={Boolean(clienteSel)}
          />
          <ErroDoCampo campo="contato_email" erros={erros} />
        </Field>
        {!clienteSel && <AvisoContatoExistente candidatos={contatoExistente.candidatos} />}

        <Field as="div" role="group" aria-labelledby="novo-lead-enderecos-rotulo">
          <span id="novo-lead-enderecos-rotulo">Endereço</span>
          <InlineHint>
            Opcional. Ao digitar o CEP, logradouro, bairro, cidade e UF são preenchidos.
          </InlineHint>
          <EnderecosEditor
            value={enderecos}
            onChange={alterarEnderecos}
            buscar={buscarEndereco}
            erros={errosEnderecos}
            idPrefix="novo-lead-enderecos"
          />
        </Field>

        <Field>
          Origem
          <Select
            {...atributosDoCampo("origem")}
            value={valores.origem}
            onChange={(e) => setCampo("origem", e.target.value)}
          >
            <option value="">— Como chegou até nós? —</option>
            {ORIGENS_LEAD.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {valores.origem === "OUTRO" && (
            <Input
              {...atributosDoCampo("origem_outro")}
              type="text"
              value={valores.origem_outro}
              onChange={(e) => setCampo("origem_outro", e.target.value)}
              placeholder="Qual a origem?"
              maxLength={120}
              style={{ marginTop: 6 }}
            />
          )}
          <ErroDoCampo campo="origem" erros={erros} />
          <ErroDoCampo campo="origem_outro" erros={erros} />
        </Field>

        <Field>
          Valor
          <Input
            {...atributosDoCampo("valor_estimado")}
            type="text"
            inputMode="numeric"
            value={valores.valor_estimado}
            onChange={(e) => {
              setCampo("valor_estimado", e.target.value);
              if (erros.valor_estimado) setErros(({ valor_estimado, ...resto }) => resto);
            }}
            onBlur={(e) => {
              // Formata no BLUR, não a cada tecla (D-6A): formatar durante a
              // digitação é o que fazia o cursor pular e o valor mudar de
              // ordem de grandeza. Negativo fica como está, com o aviso:
              // "-80000" descontava R$ 80 mil do cabeçalho do board (CRM-39).
              const n = parseDinheiro(e.target.value);
              if (n !== null && n < 0) {
                setErros((prev) => ({ ...prev, valor_estimado: MSG_VALOR_NEGATIVO }));
                return;
              }
              setCampo("valor_estimado", n === null ? "" : formatarDinheiro(n));
            }}
            placeholder="Ex.: 80.000,00"
          />
          <InlineHint>Valor previsto do negócio.</InlineHint>
          <ErroDoCampo campo="valor_estimado" erros={erros} />
        </Field>

        <Field>
          Vendedor responsavel
          <Select
            {...atributosDoCampo("responsavel")}
            value={valores.responsavel}
            onChange={(e) => setCampo("responsavel", e.target.value)}
          >
            {opcoesResponsavel.length === 0 && (
              <option value="">— Eu (padrão) —</option>
            )}
            {opcoesResponsavel.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </Select>
          <InlineHint>Por padrão, você. Pode atribuir a outro vendedor.</InlineHint>
          <ErroDoCampo campo="responsavel" erros={erros} />
        </Field>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Field style={{ flex: 2, minWidth: 160 }}>
            Próxima ação
            <Input
              {...atributosDoCampo("proxima_acao")}
              type="text"
              value={valores.proxima_acao}
              onChange={(e) => setCampo("proxima_acao", e.target.value)}
              placeholder="Ex.: Primeiro contato"
              maxLength={200}
            />
            <ErroDoCampo campo="proxima_acao" erros={erros} />
          </Field>
          <Field style={{ flex: 1, minWidth: 140 }}>
            Data
            <Input
              {...atributosDoCampo("data_proxima_acao")}
              type="date"
              value={valores.data_proxima_acao}
              onChange={(e) => setCampo("data_proxima_acao", e.target.value)}
            />
            <ErroDoCampo campo="data_proxima_acao" erros={erros} />
          </Field>
        </div>
        <InlineHint>
          Entra no sino e na Agenda na data marcada. Limpe os dois
          para criar sem follow-up.
        </InlineHint>

        <Field>
          Temperatura
          <Select
            {...atributosDoCampo("temperatura")}
            value={valores.temperatura}
            onChange={(e) => setCampo("temperatura", e.target.value)}
          >
            <option value="">— Sem definir —</option>
            {TEMPERATURAS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <ErroDoCampo campo="temperatura" erros={erros} />
        </Field>

        <Field>
          Parceiros indicadores
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value) adicionarParceiro(e.target.value);
            }}
            disabled={opcoesParceiro.length === 0}
          >
            <option value="">
              {repsDisponiveis.length === 0
                ? "— Nenhum parceiro cadastrado —"
                : "+ Adicionar parceiro…"}
            </option>
            {opcoesParceiro.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
                {r.comissao_percentual ? ` (RT ${r.comissao_percentual}%)` : ""}
              </option>
            ))}
          </Select>
          {parceirosSel.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 8,
              }}
            >
              {parceirosSel.map((p) => (
                <span
                  key={p.id}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 8px",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--surface-2, #f1f5f9)",
                    border: "1px solid var(--border, #e2e8f0)",
                  }}
                >
                  {p.nome}
                  {p.comissao_percentual ? ` · ${p.comissao_percentual}%` : ""}
                  <button
                    type="button"
                    onClick={() => removerParceiro(p.id)}
                    aria-label={`Remover ${p.nome}`}
                    style={{
                      display: "inline-flex",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      color: "var(--text-muted, #64748b)",
                    }}
                  >
                    <FiX size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <InlineHint>
            Arquiteto, decorador etc. A soma das comissões vira o RT do orçamento.
          </InlineHint>
          <ErroDoCampo campo="parceiros" erros={erros} />
        </Field>

        <Field>
          Observações
          <Textarea
            {...atributosDoCampo("observacoes")}
            value={valores.observacoes}
            onChange={(e) => setCampo("observacoes", e.target.value)}
            rows={3}
            placeholder="Notas internas sobre este lead"
          />
          <ErroDoCampo campo="observacoes" erros={erros} />
        </Field>

        <ErroDoCampo campo="cliente" erros={erros} />

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            paddingTop: 4,
          }}
        >
          <button
            className="btn btn--secondary"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button className="btn btn--primary" type="submit" disabled={loading}>
            {loading ? "Criando..." : "Criar lead"}
          </button>
        </div>
      </FormStack>
    </form>
  );
}

export default NovoLeadForm;
